import { Routes, Route, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { lazy, Suspense, memo } from "react";
import { RecoilRoot } from "recoil";
import LoadingFallback from "./components/ui/LoadingFallback";
import SentryFallback from "./components/ui/SentryFallback";
import { Toaster } from "react-hot-toast";
import { AILandingPage, AIChatPage } from "./components/Resources/AIChat";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PremiumProvider } from "./contexts/PremiumContext";
import { Auth0Provider } from "@auth0/auth0-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import CloudDashboard from "./pages/CloudDashboard";
import { FeatureFlagProvider } from "./components/Flags/FeatureFlags";
import CloudProviderSetup from "./pages/CloudProviderSetup";
import MainLayout from "./components/Layout/MainLayout";
import PrivateLayout from "./pages/PrivateLayer";
import { useFeatureFlag, FEATURE_FLAGS } from "./hooks/useFeatureFlag";
import { useProviderCredentials } from "./hooks/useProviderCredentials";

const SettingsPage = lazy(
  () => import("./pages/SettingsPage" /* webpackChunkName: "settings" */)
);
const ProjectsPage = lazy(
  () => import("./pages/ProjectsPage" /* webpackChunkName: "projects" */)
);
const ProjectDetailsPage = lazy(
  () =>
    import(
      "./pages/ProjectDetailsPage" /* webpackChunkName: "project-details" */
    )
);
const OnboardingPage = lazy(
  () => import("./pages/onBoarding" /* webpackChunkName: "onboarding" */)
);

// Create a query client instance with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes to free memory when not needed
      retry: 1,
    },
  },
});

// Optimized PrivateRoute component with memoization
const PrivateRoute = memo(({ children }: { children: React.ReactNode }) => {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return <LoadingFallback />;
  }

  return isAuthenticated ? (
    children
  ) : (
    <Navigate to="/login" replace state={{ returnTo: location.pathname }} />
  );
});

// Route components with individual Suspense boundaries
const RouteWithSuspense = memo(
  ({
    element,
    fallback = <LoadingFallback />,
    private: isPrivate = true,
    withLayout = true,
  }: {
    element: React.ReactNode;
    fallback?: React.ReactNode;
    private?: boolean;
    withLayout?: boolean;
  }) => {
    let content = isPrivate ? <PrivateRoute>{element}</PrivateRoute> : element;

    // Wrap authenticated routes in MainLayout (shows sidebar)
    if (isPrivate && withLayout) {
      content = <MainLayout>{content}</MainLayout>;
    }

    return <Suspense fallback={fallback}>{content}</Suspense>;
  }
);

// Main routing component separated from state initialization
const AppRoutes = memo(() => {
  const { token, isLoading: authLoading } = useAuth();
  const { isEnabled: isCloudBuilderEnabled, isLoading: isCloudBuilderLoading } =
    useFeatureFlag(FEATURE_FLAGS.CLOUD_BUILDER);
  const { data: credentials, isLoading: credsLoading } = useProviderCredentials();
  const isConfigured = credentials?.some((cred) => cred.created_at) ?? false;
  return (
    <Routes>
      {/* public login route */}
      <Route path="/login" element={<LoginPage />} />

      {/* all authenticated pages share PrivateLayout (auth guard, sidebar, error boundary) */}
      <Route element={<PrivateLayout />}>
        <Route
          index
          element={
            // Wait for auth token and feature flags/credentials to load before routing
            authLoading || !token || isCloudBuilderLoading || credsLoading ? (
              <LoadingFallback />
            ) : !isCloudBuilderEnabled ? (
              <Navigate to="/chat-dashboard" replace />
            ) : !isConfigured ? (
              <Navigate to="/onboarding" replace />
            ) : (
              <CloudDashboard />
            )
          }
        />
        <Route path="chat-dashboard" element={<AILandingPage />} />
        <Route path="chat/:conversationId" element={<AIChatPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="cloud-provider-setup" element={<CloudProviderSetup />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailsPage />} />
      </Route>

      {/* fallback for unmatched */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
});

// Main content component with error boundary
const AppContent = memo(() => {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <AppRoutes />
    </Sentry.ErrorBoundary>
  );
});

// Root App component with all providers
function App() {
  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <Auth0Provider
          domain={import.meta.env.VITE_AUTH0_DOMAIN || ""}
          clientId={import.meta.env.VITE_AUTH0_CLIENT_ID || ""}
          authorizationParams={{
            redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI || "",
            audience: import.meta.env.VITE_AUTH0_AUDIENCE || "",
            scope: "openid profile email offline_access",
          }}
          useRefreshTokens={true}
        >
          <AuthProvider>
            <PremiumProvider>
              <FeatureFlagProvider>
                <AppContent />
              </FeatureFlagProvider>
              <Toaster position="top-right" />
            </PremiumProvider>
          </AuthProvider>
        </Auth0Provider>
      </QueryClientProvider>
    </RecoilRoot>
  );
}

export default App;
